<template>
  <div class="teacher-home teacher-fine-home">
    <div class="layout clearfix">

      <!-- ══════════════════════════════
           HERO — 教师工作台
      ══════════════════════════════ -->
      <div class="teacher-hero-fine">
        <!-- 背景装饰 -->
        <div class="th-grid"></div>
        <div class="th-glow th-glow-l"></div>
        <div class="th-glow th-glow-r"></div>

        <!-- 左侧文案 -->
        <div class="hero-copy">
          <span class="hero-kicker">
            <span class="kicker-dot"></span>
            Teacher Workspace
          </span>
          <h1>教师工作台</h1>
          <p>课程维护、案例管理与学习成效查看的教学管理统一入口。</p>
        </div>

        <!-- 右侧指标面板 -->
        <div class="hero-metrics">
          <div class="hm-item">
            <span class="hm-label">编辑中</span>
            <strong class="hm-val">{{ editingCourses.length }}</strong>
          </div>
          <div class="hm-sep"></div>
          <div class="hm-item">
            <span class="hm-label">考核中</span>
            <strong class="hm-val">{{ reviewingTotal || reviewingCourses.length }}</strong>
          </div>
          <div class="hm-sep"></div>
          <div class="hm-item">
            <span class="hm-label">历史课程</span>
            <strong class="hm-val">{{ historyTotal }}</strong>
          </div>
          <div class="hm-sep"></div>
          <div class="hm-item">
            <span class="hm-label">教学管理</span>
            <strong class="hm-val hm-online">在线</strong>
          </div>
        </div>
      </div>

      <!-- ══════════════════════════════
           顶部主区：编辑中课程 + 教师信息
      ══════════════════════════════ -->
      <div class="section clearfix">

        <!-- 编辑中课程 -->
        <div class="editing common-bg">
          <div class="panel-header">
            <div class="panel-header-left">
              <span class="sec-kicker">Editing</span>
              <div class="title">
                编辑中
                <el-button
                  type="primary"
                  style="margin-left: 14px"
                  @click="showAddCourseModal"
                  icon="el-icon-plus"
                  size="small"
                >
                  新增课程
                </el-button>
              </div>
            </div>
            <span class="panel-badge">{{ editingCourses.length }} 门</span>
          </div>
          <div class="imgtxt-list">
            <div class="item" v-for="(course, index) in editingCourses" :key="index">
              <CourseCard :course="course" />
            </div>
            <!-- 空状态 -->
            <div v-if="editingCourses.length === 0" class="panel-empty">
              <div class="pe-icon"><i class="el-icon-edit-outline"></i></div>
              <p>暂无编辑中的课程，点击"新增课程"开始创建。</p>
            </div>
          </div>
        </div>

        <!-- 教师信息卡 -->
        <div class="user-info">
          <div class="ui-header">
            <span class="ui-dot"></span>
            <span class="ui-label">教师信息</span>
          </div>
          <div class="greet-info">你好，</div>
          <div class="user-name">{{ user.nickName || '教师用户' }}</div>
          <div class="college-name" v-if="user.dept">{{ user.dept.deptName }}</div>
          <div class="ui-divider"></div>
          <div class="contact-info">
            欢迎回到智训立方课程制作空间。您可以在这里维护应急演训课程、管理案例内容、提交审核并查看学习反馈。
          </div>
          <div class="email">
            <a :href="`mailto:${contactEmail}`">
              <i class="el-icon-message"></i>{{ contactEmail }}
            </a>
          </div>
        </div>
      </div>

      <!-- ══════════════════════════════
           考核中课程
      ══════════════════════════════ -->
      <div class="under-review common-bg">
        <div class="panel-header">
          <div class="panel-header-left">
            <span class="sec-kicker">Assessing</span>
            <div class="title">考核中</div>
          </div>
          <span class="panel-badge badge-orange">{{ reviewingTotal || reviewingCourses.length }} 门</span>
        </div>
        <div class="imgtxt-list clearfix">
          <div class="item" v-for="(course, index) in reviewingCourses" :key="index">
            <CourseCard :course="course" />
          </div>
          <div v-if="reviewingCourses.length === 0" class="panel-empty">
            <div class="pe-icon"><i class="el-icon-document-checked"></i></div>
            <p>暂无考核中的课程。</p>
          </div>
        </div>
        <Pagination
          :total-items="reviewingTotal"
          :items-per-page="reviewingPerPage"
          :current-page="reviewingPage"
          @page-changed="changeReviewingPage"
        />
      </div>

      <!-- ══════════════════════════════
           历史课程
      ══════════════════════════════ -->
      <div class="history-course common-bg">
        <div class="panel-header">
          <div class="panel-header-left">
            <span class="sec-kicker">History</span>
            <div class="title">历史课程</div>
          </div>
          <span class="panel-badge badge-gray">{{ historyTotal }} 门</span>
        </div>
        <div class="imgtxt-list clearfix" v-if="historyCourses.length > 0">
          <div class="item" v-for="(course, index) in historyCourses" :key="index">
            <CourseCard :course="course" />
          </div>
        </div>
        <div v-else class="no-data">
          <el-empty description="暂无历史课程"></el-empty>
        </div>
        <Pagination
          :total-items="historyTotal"
          :items-per-page="historyPerPage"
          :current-page="historyPage"
          @page-changed="changeHistoryPage"
        />
      </div>
    </div>

    <!-- ══════════════════════════════
         新增课程弹窗（el-dialog 原样保留）
    ══════════════════════════════ -->
    <el-dialog
      title="新增课程"
      :visible.sync="showAddCourse"
      width="50%"
      :close-on-click-modal="false"
    >
      <el-form :model="newCourse" label-width="120px">
        <el-form-item label="课程名称：">
          <el-input v-model="newCourse.title" placeholder="请输入课程名称"></el-input>
        </el-form-item>
        <el-form-item label="课程封面：">
          <span class="tip">建议尺寸16:9</span>
          <imageUpload @input="handleFileUpload"></imageUpload>
        </el-form-item>
        <el-form-item label="课程简述：">
          <el-input
            type="text"
            maxlength="40"
            show-word-limit
            v-model="newCourse.summary"
            placeholder="请输入课程简述"
          ></el-input>
        </el-form-item>
        <el-form-item label="课程说明：">
          <el-input
            type="textarea"
            :rows="4"
            v-model="newCourse.content"
            placeholder="请输入课程说明"
          ></el-input>
        </el-form-item>
        <el-form-item label="标签：">
          <el-select v-model="newCourse.tagsIds" style="width: 100%" multiple placeholder="请选择标签">
            <el-option
              v-for="item in tags"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="是否匿名访问：">
          <el-radio-group v-model="newCourse.anon">
            <el-radio :label="0">不可以</el-radio>
            <el-radio :label="1">可以</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item>
          <div class="btns">
            <el-button @click="closeAddCourseModal">取消</el-button>
            <el-button type="primary" @click="createCourse">创建新课程</el-button>
          </div>
        </el-form-item>
      </el-form>
    </el-dialog>
  </div>
</template>

<script>
import { mapState } from "vuex";
import CourseCard from "@/components/CourseCard";
import Pagination from "@/components/Pagination";
import {
  getContentHistories,
  getContentAssessments,
  getContentEditings,
  addCourse,
  getContentTags,
} from "@/api/index";
export default {
  name: "TeacherHome",
  components: {
    CourseCard,
    Pagination,
  },
  data() {
    return {
      contactEmail: "support@zhi119.com",
      showAddCourse: false,
      newCourse: {
        title: "",
        imgUrl: "",
        summary: "",
        content: "",
        anon: 1, // 是否匿名访问，默认可以
        tagsIds: [], // 标签ID数组
      },
      // 编辑中的课程
      editingCourses: [],
      // 考核中的课程
      reviewingCourses: [],
      reviewingTotal: 0,
      reviewingPerPage: 3,
      reviewingPage: 1,
      // 历史课程
      historyCourses: [],
      historyTotal: 0,
      historyPerPage: 3,
      historyPage: 1,
      tags: [],
    };
  },
  mounted() {
    this.fetchReviewingCourses();
    this.fetchHistoryCourses();
    this.fetchEditingCourses();
    this.fetchTags();
  },
  computed: {
    ...mapState(["user"]),
  },
  methods: {
    // 获取标签列表
    fetchTags() {
      getContentTags().then((res) => {
        if (res.code !== 200) {
          this.$message.error("获取标签列表失败");
          return;
        }
        this.tags = res.data || [];
      });
    },
    fetchEditingCourses() {
      getContentEditings().then((res) => {
        this.editingCourses = res.data || [];
      });
    },
    fetchReviewingCourses() {
      getContentAssessments({
        pageNum: this.reviewingPage,
        pageSize: this.reviewingPerPage,
      })
        .then((response) => {
          if (response.code == 200) {
            this.reviewingTotal = response.total;
            this.reviewingCourses = response.rows || [];
          }
        })
        .catch((error) => {
          console.error("获取考核课程失败:", error);
        });
    },
    fetchHistoryCourses() {
      getContentHistories({
        pageNum: this.historyPage,
        pageSize: this.historyPerPage,
      })
        .then((response) => {
          if (response.code == 200) {
            this.historyTotal = response.total;
            this.historyCourses = response.rows || [];
          }
        })
        .catch((error) => {
          console.error("获取历史课程失败:", error);
        });
    },
    changeReviewingPage(page) {
      this.reviewingPage = page;
      this.fetchReviewingCourses();
    },
    changeHistoryPage(page) {
      this.historyPage = page;
      this.fetchHistoryCourses();
    },
    showAddCourseModal() {
      this.showAddCourse = true;
    },
    closeAddCourseModal() {
      this.showAddCourse = false;
      this.resetNewCourse();
    },
    resetNewCourse() {
      this.newCourse = {
        title: "",
        imgUrl: "",
        summary: "",
        content: "",
        anon: 1, // 是否匿名访问，默认可以
        tagsIds: [], // 标签ID数组
      };
    },
    handleFileUpload(file) {
      this.newCourse.imgUrl = file;
    },
    createCourse() {
      if (!this.newCourse.title.trim()) {
        this.$message.error("请输入课程名称");
        return;
      }
      if (!this.newCourse.imgUrl) {
        this.$message.error("请上传课程封面");
        return;
      }
      const params = {
        ...this.newCourse,
      };
      addCourse(params)
        .then((response) => {
          if (response.code === 200) {
            this.$message.success("课程创建成功");
            this.closeAddCourseModal();
            this.fetchEditingCourses(); // 刷新编辑中的课程
          } else {
            this.$message.error(response.msg || "课程创建失败");
          }
        })
        .catch((error) => {
          console.error("创建课程失败:", error);
          this.$message.error("课程创建失败，请稍后再试");
        });
    },
  },
};
</script>

<style scoped lang="scss">
/* ─────────────────────────────────────────
   Design tokens
───────────────────────────────────────── */
.teacher-fine-home {
  --bd:      #081c42;
  --bm:      #1557d8;
  --ba:      #1e9bff;
  --cyan:    #20c8ff;
  --gray:    #475569;
  --gray-s:  #64748b;
  --card-bg: rgba(255,255,255,0.92);
  --card-bdr: rgba(168,199,245,0.68);

  min-height: 100vh;
  padding: 30px 24px 70px;
  background:
    radial-gradient(ellipse at 8% 0%, rgba(21,87,216,0.09), transparent 34%),
    radial-gradient(ellipse at 94% 3%, rgba(32,200,255,0.09), transparent 30%),
    linear-gradient(180deg, #f2f7ff 0%, #f5f7fb 48%, #ffffff 100%);
}

.layout {
  width: min(1180px, 100%);
  margin: 0 auto;
}
.clearfix::after { content: ""; display: table; clear: both; }

/* ─────────────────────────────────────────
   HERO
───────────────────────────────────────── */
.teacher-hero-fine {
  position: relative;
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 20px;
  padding: 24px 26px;
  border-radius: 22px;
  background: linear-gradient(150deg, rgba(8,22,58,0.97), rgba(10,52,112,0.92));
  border: 1px solid rgba(32,200,255,0.16);
  color: #fff;
  box-shadow: 0 22px 48px rgba(4,14,44,0.20);
  overflow: hidden;
}

.th-grid {
  position: absolute;
  inset: 0;
  opacity: 0.32;
  background-image:
    linear-gradient(rgba(255,255,255,0.07) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.07) 1px, transparent 1px);
  background-size: 40px 40px;
  pointer-events: none;
}
.th-glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(16px);
  pointer-events: none;
}
.th-glow-l { width: 260px; height: 260px; left: -80px; bottom: -80px; background: rgba(21,87,216,0.22); }
.th-glow-r { width: 200px; height: 200px; right: -40px; top: -40px; background: rgba(32,200,255,0.18); }

.hero-copy { position: relative; z-index: 1; flex: 1; }

.hero-kicker {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 11px;
  padding: 4px 11px;
  border-radius: 999px;
  background: rgba(255,255,255,0.10);
  color: rgba(255,255,255,0.78);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.5px;
}
.kicker-dot {
  width: 6px; height: 6px;
  border-radius: 50%;
  background: var(--cyan);
  box-shadow: 0 0 8px rgba(32,200,255,0.9);
}

.teacher-hero-fine h1 {
  margin: 0 0 8px;
  font-size: 26px;
  line-height: 1.2;
  font-weight: 950;
}
.teacher-hero-fine p {
  margin: 0;
  color: rgba(255,255,255,0.72);
  font-size: 13px;
  line-height: 1.78;
  max-width: 520px;
}

/* Right metrics */
.hero-metrics {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-radius: 16px;
  background: rgba(255,255,255,0.07);
  border: 1px solid rgba(255,255,255,0.10);
  backdrop-filter: blur(8px);
  align-self: center;
  flex-shrink: 0;
}
.hm-item { text-align: center; min-width: 58px; }
.hm-label {
  display: block;
  font-size: 11px;
  color: rgba(255,255,255,0.60);
  margin-bottom: 5px;
  font-weight: 700;
}
.hm-val {
  display: block;
  font-size: 22px;
  font-weight: 950;
  color: #fff;
  line-height: 1;
}
.hm-online { color: #4ade80; }
.hm-sep { width: 1px; height: 40px; background: rgba(255,255,255,0.12); }

/* ─────────────────────────────────────────
   LAYOUT SECTION (编辑中 + 用户信息)
───────────────────────────────────────── */
.section {
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 18px;
  margin-bottom: 18px;
}

.common-bg,
.user-info {
  border-radius: 20px;
  background: var(--card-bg);
  border: 1px solid var(--card-bdr);
  box-shadow: 0 12px 32px rgba(8,28,66,0.08);
}

.editing,
.under-review,
.history-course {
  padding: 22px 24px;
}
.under-review,
.history-course { margin-bottom: 18px; }

/* Panel header */
.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 18px;
  padding-bottom: 14px;
  border-bottom: 1px solid rgba(168,199,245,0.38);
}
.panel-header-left {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.sec-kicker {
  color: var(--ba);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.5px;
}
.title {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--bd);
  font-size: 18px;
  font-weight: 950;
  margin: 0;
}
.panel-badge {
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(21,87,216,0.08);
  border: 1px solid rgba(21,87,216,0.14);
  color: var(--bm);
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
  flex-shrink: 0;
}
.badge-orange {
  background: rgba(251,146,60,0.09);
  border-color: rgba(251,146,60,0.22);
  color: #c2410c;
}
.badge-gray {
  background: rgba(100,116,139,0.08);
  border-color: rgba(100,116,139,0.18);
  color: var(--gray-s);
}

/* Course card grid */
.imgtxt-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
}
.imgtxt-list .item { min-width: 0; }

/* Panel empty state */
.panel-empty {
  grid-column: 1 / -1;
  padding: 28px;
  border-radius: 16px;
  background: rgba(248,251,255,0.9);
  border: 1px dashed rgba(21,87,216,0.18);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
}
.pe-icon {
  width: 48px; height: 48px;
  border-radius: 16px;
  display: grid;
  place-items: center;
  background: linear-gradient(145deg, rgba(21,87,216,0.10), rgba(32,200,255,0.12));
  color: var(--ba);
  font-size: 22px;
}
.panel-empty p { color: var(--gray-s); font-size: 13px; line-height: 1.72; margin: 0; }

/* ─────────────────────────────────────────
   USER INFO CARD
───────────────────────────────────────── */
.user-info {
  padding: 22px;
  background: linear-gradient(150deg, rgba(8,22,58,0.96), rgba(10,52,112,0.92));
  border-color: rgba(32,200,255,0.16);
  color: #fff;
  position: relative;
  overflow: hidden;
}
.user-info::after {
  content: "";
  position: absolute;
  right: -50px; top: -50px;
  width: 150px; height: 150px;
  border-radius: 50%;
  background: rgba(32,200,255,0.12);
  filter: blur(4px);
  pointer-events: none;
}
.ui-header {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 14px;
}
.ui-dot {
  width: 7px; height: 7px;
  border-radius: 50%;
  background: var(--cyan);
  box-shadow: 0 0 8px rgba(32,200,255,0.9);
}
.ui-label { font-size: 11px; color: rgba(255,255,255,0.60); font-weight: 700; }
.greet-info { color: rgba(255,255,255,0.68); font-size: 12px; font-weight: 700; }
.user-name { position: relative; z-index: 1; color: #fff; font-size: 20px; font-weight: 950; line-height: 1.25; margin: 6px 0 6px; }
.college-name { position: relative; z-index: 1; color: rgba(255,255,255,0.78); font-size: 13px; margin-bottom: 0; }
.ui-divider { height: 1px; background: rgba(255,255,255,0.10); margin: 14px 0; }
.contact-info { position: relative; z-index: 1; color: rgba(255,255,255,0.70); line-height: 1.78; font-size: 12px; }
.email { position: relative; z-index: 1; margin-top: 14px; }
.email a {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #fff;
  font-size: 12px;
  font-weight: 800;
  text-decoration: none;
  transition: color 0.2s;
}
.email a:hover { color: var(--cyan); }

.no-data { padding: 20px 0; }

/* ─────────────────────────────────────────
   DIALOG
───────────────────────────────────────── */
.btns {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  flex-wrap: nowrap;
}
.btns ::v-deep .el-button {
  margin-left: 0 !important;
  min-width: 92px;
  height: 34px;
  border-radius: 10px;
  font-weight: 800;
}
.tip { display: inline-block; color: var(--gray-s); font-size: 13px; margin-bottom: 8px; }

::v-deep .el-dialog {
  border-radius: 20px;
  overflow: hidden;
}
::v-deep .el-dialog__header {
  padding: 20px 24px;
  background: #f8fafc;
  border-bottom: 1px solid rgba(168,199,245,0.5);
}
::v-deep .el-dialog__title {
  color: var(--bd);
  font-weight: 900;
  font-size: 16px;
}

/* ─────────────────────────────────────────
   RESPONSIVE
───────────────────────────────────────── */
@media (max-width: 980px) {
  .section { grid-template-columns: 1fr; }
  .teacher-hero-fine { flex-direction: column; }
  .hero-metrics { align-self: flex-start; width: 100%; justify-content: space-around; }
  .teacher-fine-home { padding: 24px 16px 56px; }
}
@media (max-width: 640px) {
  .imgtxt-list { grid-template-columns: 1fr; }
  .hero-metrics { flex-wrap: wrap; gap: 16px; }
  .hm-sep { display: none; }
}
</style>
